import { useCallback } from 'react';
import { useAppDispatch } from '../stores/redux.store';
import { openAwsCaptchaModal } from './aws-captcha-actions';
import { useAwsCaptchaSuccessCallbackRef } from './aws-captcha-success-callback-context';

/**
 * Returns a function that opens the AWS Captcha modal and stores the onSuccess
 * callback so that it can be called to retry the original request after the user solves the puzzle.
 *
 * It cannot be stored in Redux due to the fact that callbacks are not serializable, so we use a ref to store it instead.
 *
 * The onSuccess callback is cleared after it is called, so it will only be called once per modal open.
 *
 * The onSuccess callback _should_ be the function that retries whatever the original user's action was (e.g. retrying the original HTTP request)
 * so that the user doesn't have to manually retry after solving the puzzle.
 */
export const useOpenAwsCaptchaModalCallback = () => {
  const dispatch = useAppDispatch();
  const successCallbackRef = useAwsCaptchaSuccessCallbackRef();

  return useCallback(
    ({ onSuccess }: { onSuccess: () => void }) => {
      successCallbackRef.current = onSuccess;
      dispatch(openAwsCaptchaModal());
    },
    [dispatch, successCallbackRef]
  );
};
